import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import styled from 'styled-components'

import MasterLayout from '../layouts'
import CaseTeaser from '../components/CaseTeaser'
import ParticleTitle from '../components/ParticleTitle'
import { flattenObject, getSlug, extractMetadata } from '../utils'

const CasesPage = ({
  data: { casesOverview, prismicNavigation },
  pageContext: { breadcrumbs },
}) => {
  const { alternate_languages: altLanguages } = casesOverview

  return (
    <MasterLayout
      altLanguages={altLanguages[0]}
      breadcrumbs={breadcrumbs}
      prismicNavigation={prismicNavigation.content.items}
      metadata={extractMetadata(
        casesOverview.content,
        casesOverview.content.title.text,
      )}
    >
      <Fragment>
        <ParticleTitle
          particleSize={0.8}
          title={casesOverview.content.title.text}
          particles={[
            {
              particle_variant: 'ParticleCube',
              particle_color: '#334479',
              particle_type: 'small',
              particle_position: {
                right: 150,
                bottom: 40,
              },
              particle_mobile_position: {
                left: 250,
                top: 140,
              },
            },
            {
              particle_variant: 'ParticleCube',
              particle_color: '#1BA9C3',
              particle_type: 'large',
              particle_position: {
                right: 100,
                bottom: 0,
              },
              particle_mobile_position: {
                left: 280,
                top: 170,
              },
            },
            {
              particle_variant: 'ParticleRectangle',
              particle_color: '#F8B8AC',
              particle_type: 'large',
              particle_position: {
                left: -60,
                bottom: 150,
              },
              particle_mobile_position: {
                left: 40,
                top: 50,
              },
            },
          ]}
        />

        <CasesWrapper>
          {casesOverview.content.cases.map(
            node =>
              node.case && (
                <CaseTeaser
                  content={flattenObject(node.case)}
                  key={Math.random()}
                  slug={getSlug(node.case)}
                />
              ),
          )}
        </CasesWrapper>
      </Fragment>
    </MasterLayout>
  )
}

CasesPage.propTypes = {
  data: PropTypes.shape({
    casesOverview: PropTypes.shape({
      alternate_languages: PropTypes.arrayOf(
        PropTypes.shape({
          lang: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
        }),
      ),
      content: PropTypes.shape({
        cases: PropTypes.arrayOf(
          PropTypes.shape({
            case: PropTypes.shape({}).isRequired,
          }).isRequired,
        ).isRequired,
        title: PropTypes.shape({
          text: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
    prismicNavigation: PropTypes.shape({
      content: PropTypes.shape({
        items: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
          }).isRequired,
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    breadcrumbs: PropTypes.shape({}),
  }).isRequired,
}

const CasesWrapper = styled.div`
  display: grid;
  grid-gap: 0;
  grid-auto-flow: dense;
  margin: 0 auto;
  max-width: 1600px;

  @media (min-width: 680px) {
    grid-template-columns: 1fr 1fr;
  }
`

export const query = graphql`
  query casesQuery {
    casesOverview: prismicCasesOverview {
      alternate_languages {
        lang
        type
      }
      content: data {
        title {
          text
        }
        cases {
          case {
            document {
              slug: uid
              data {
                title {
                  text
                }
                client {
                  text
                }
                hero {
                  localFile {
                    childImageSharp {
                      fluid(maxWidth: 1400) {
                        ...GatsbyImageSharpFluid_noBase64
                      }
                    }
                  }
                }
                logo {
                  alt
                  url
                }
                teaserImage: teaser_image {
                  localFile {
                    childImageSharp {
                      fluid(maxWidth: 1400) {
                        ...GatsbyImageSharpFluid_noBase64
                      }
                    }
                  }
                }
              }
            }
          }
        }
        metaTitle: meta_title
        metaDescription: meta_description
        metaRobots: meta_robots
        metaImage: meta_image {
          url
        }
      }
    }
    prismicNavigation: prismicNavigation {
      content: data {
        items {
          name
          description
          url
        }
      }
    }
  }
`

export default CasesPage
